import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { patchActivityMemoryEnabled } from '@/api/activity';
import { SettingsToggleRow } from './SettingsToggleRow';
import { notifications } from '@/utils/notifications';

type PreferenceResponse = {
  status: string;
  preference?: {
    summarizeOnSave?: boolean;
    digestWeeklyEnabled?: boolean;
  };
};

export function WorkMemorySettings() {
  const { t } = useTranslation('settings');
  const [loading, setLoading] = useState(true);
  const [summarizeOnSave, setSummarizeOnSave] = useState(false);
  const [digestWeeklyEnabled, setDigestWeeklyEnabled] = useState(true);
  const [activityMemoryEnabled, setActivityMemoryEnabled] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [prefRes, memRes] = await Promise.all([
        api.get<PreferenceResponse>('/preference'),
        api.get<{ activityMemoryEnabled?: boolean }>('/activity/welcome-back'),
      ]);
      const pref = prefRes.data?.preference;
      if (pref) {
        setSummarizeOnSave(pref.summarizeOnSave === true);
        setDigestWeeklyEnabled(pref.digestWeeklyEnabled !== false);
      }
      if (memRes.data?.activityMemoryEnabled === false) {
        setActivityMemoryEnabled(false);
      } else {
        setActivityMemoryEnabled(true);
      }
    } catch {
      notifications.error(t('workMemory.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const savePref = async (patch: { summarizeOnSave?: boolean; digestWeeklyEnabled?: boolean }) => {
    await api.put('/preference', patch);
  };

  return (
    <section className="space-y-1 rounded-2xl border border-border bg-card p-2 sm:p-3" aria-labelledby="work-memory-settings-title">
      <div className="px-3 pt-2">
        <h2 id="work-memory-settings-title" className="text-base font-bold text-foreground">
          {t('workMemory.title')}
        </h2>
        <p className="mt-0.5 text-xs text-muted-foreground">{t('workMemory.subtitle')}</p>
      </div>

      <SettingsToggleRow
        title={t('workMemory.activityMemoryLabel')}
        description={t('workMemory.activityMemoryDesc')}
        checked={activityMemoryEnabled}
        disabled={loading}
        ariaLabel={t('workMemory.activityMemoryLabel')}
        onChange={() => {
          const next = !activityMemoryEnabled;
          void (async () => {
            try {
              await patchActivityMemoryEnabled(next);
              setActivityMemoryEnabled(next);
              notifications.success(t('workMemory.saved'));
            } catch {
              notifications.error(t('workMemory.saveError'));
            }
          })();
        }}
      />

      <SettingsToggleRow
        title={t('workMemory.summarizeOnSaveLabel')}
        description={t('workMemory.summarizeOnSaveDesc')}
        checked={summarizeOnSave}
        disabled={loading}
        ariaLabel={t('workMemory.summarizeOnSaveLabel')}
        onChange={() => {
          const next = !summarizeOnSave;
          void (async () => {
            try {
              await savePref({ summarizeOnSave: next });
              setSummarizeOnSave(next);
              notifications.success(t('workMemory.saved'));
            } catch {
              notifications.error(t('workMemory.saveError'));
            }
          })();
        }}
      />

      <SettingsToggleRow
        title={t('workMemory.digestWeeklyLabel')}
        description={t('workMemory.digestWeeklyDesc')}
        checked={digestWeeklyEnabled}
        disabled={loading}
        ariaLabel={t('workMemory.digestWeeklyLabel')}
        onChange={() => {
          const next = !digestWeeklyEnabled;
          void (async () => {
            try {
              await savePref({ digestWeeklyEnabled: next });
              setDigestWeeklyEnabled(next);
              notifications.success(t('workMemory.saved'));
            } catch {
              notifications.error(t('workMemory.saveError'));
            }
          })();
        }}
      />
    </section>
  );
}
